import type { UIKeyValueSorterSortingInstruction } from "uicore-ts"
import { IF, NO, UITextView, UIView, YES } from "uicore-ts"
import { LanguageService } from "../../LanguageService"
import { CBDataView, CBDataViewCellDescriptor, CBSelectionColumnDescriptor } from "../CB/CBDataView"
import type { CBDataViewRow } from "../CB/CBTableRowView"
import { ColumnCellDescriptor } from "../ColumnView"
import { RHCheckbox } from "./RHCheckbox"
import { RHTableHeaderView } from "./RHTableHeaderView"
import { RHTableRowView } from "./RHTableRowView"
import { RHTextField } from "./RHTextField"
import { RHTables } from "./Utils_slop/RHDesignTokens"

export type RHDataViewPageScrollForwardingMode =
    "disabled" |
    "chained" |
    "revealThenBlockPage" |
    "revealThenPreferDataview" |
    "pageFirst"


/** Numeric row fields and compound instructions that keep missing values last. */
export class RHNumberSorting {

    static readonly valueKeyPath = "_rhNumberSortValue"
    static readonly availabilityKeyPath = "_rhNumberSortAvailability"


    static fields(value: number | undefined): { _rhNumberSortValue: number, _rhNumberSortAvailability: number } {
        let sortableValue = 0
        let availability = 0

        if (typeof value === "number" && Number.isFinite(value)) {
            sortableValue = value
            availability = 1
        }

        return {
            [this.valueKeyPath]: sortableValue,
            [this.availabilityKeyPath]: availability
        }
    }


    static instructions(orderingState: string): UIKeyValueSorterSortingInstruction[] {
        if (orderingState === "unordered") {
            return []
        }

        return [
            {
                keyPath: this.availabilityKeyPath,
                dataType: "number",
                direction: "descending"
            },
            {
                keyPath: this.valueKeyPath,
                dataType: "number",
                direction: orderingState
            }
        ]
    }

}

/**
 * RHDataView
 * Styles: Bootstrap .card
 * Uses RHTextField for the search bar.
 */
export class RHDataView<DataType extends object = object> extends CBDataView<DataType> {

    static readonly pageScrollBoundaryEpsilon = 1
    static readonly pageScrollWheelGestureResetInterval = 160
    static _revealThenPreferDataviewWheelGestureStates = new WeakMap<
        RHDataView<object>,
        { lastWheelEventTime: number; didStartInRevealedState: boolean }
    >()
    static _pageScrollChainDataViews = new Set<RHDataView<object>>()
    
    // Override the property type to use the specialized component
    override searchTextField: RHTextField
    override tableHeaderView: RHTableHeaderView<DataType>
    usesRowCardMode = false
    pageScrollForwardingMode: RHDataViewPageScrollForwardingMode = "chained"
    /**
     * Optional visible descriptor key path whose cell owns the tree disclosure.
     * Undefined keeps the standard CB tree layout unchanged.
     */
    treeDisclosureColumnKeyPath?: string
    additionalSearchTextForDataItem?: (dataItem: DataType) => string

    // Optional opt-in control laid out as a row directly below the search field (and
    // above the table). Used e.g. by the global procurement search to host the small
    // "Otsi CPV koodi järgi" chip. Undefined for every other data view, so the default
    // layout is unchanged.
    private _searchAccessoryView?: UIView

    get searchAccessoryView(): UIView | undefined {
        return this._searchAccessoryView
    }

    set searchAccessoryView(view: UIView | undefined) {
        this._searchAccessoryView?.removeFromSuperview()
        this._searchAccessoryView = view
        if (view) {
            this.addSubview(view)
        }
        this.setNeedsLayout()
    }


    override _searchStringsForData(data: DataType[]): string[] {
        const searchStrings = super._searchStringsForData(data)
        const additionalSearchTextForDataItem = this.additionalSearchTextForDataItem
        if (!additionalSearchTextForDataItem) {
            return searchStrings
        }
        return searchStrings.map((text, index) => text + " " + additionalSearchTextForDataItem(data[index]))
    }


    constructor(elementID?: string) {
        
        super(elementID)
        RHDataView._pageScrollChainDataViews.add(this)
        
        this.selectionCheckboxClass = RHCheckbox;
        
        // 1. Container Configuration (Bootstrap Card)
        (this as RHDataView<DataType>).configureWithObject({
            backgroundColor: RHTables.dataViewBackgroundColor,
            style: {
                // display: "flex",
                // flexDirection: "column",
                // minWidth: "0",
                // wordWrap: "break-word",
                // backgroundClip: "border-box",
                // border: `1px solid ${BSColor.borderLight.stringValue}`,
                // borderRadius: "0.375rem",
                // boxShadow: "none"
                // RHPanelView style
                borderRadius: RHTables.dataViewBorderRadius,
                border: `1px solid ${RHTables.dataViewBorderColor.stringValue}`,
                boxShadow: RHTables.dataViewBoxShadow
            }
        })
        
        // 2. Title Configuration
        this.titleLabel.configureWithObject({
            textColor: RHTables.dataViewTitleTextColor,
            style: {
                fontSize: RHTables.dataViewTitleFontSize,
                fontWeight: RHTables.dataViewTitleFontWeight,
                fontFamily: RHTables.dataViewTitleFontFamily,
                marginBottom: RHTables.dataViewTitleMarginBottom,
                lineHeight: RHTables.dataViewTitleLineHeight
            }
        })
        
        // 3. Search Bar Configuration
        // We replace the generic UITextField with RHTextField
        // @ts-ignore
        this.searchTextField.removeFromSuperview()
        
        // Instantiate the specialized RHTextField
        // It already handles borders, padding, focus rings, and validation internally.
        this.searchTextField = new RHTextField(this.elementID + "SearchTextField")
        this.addSubview(this.searchTextField)
        
        this.searchTextField.configureWithObject({
            placeholderText: LanguageService.stringForKey(
                "CBTableViewSearch",
                LanguageService.currentLanguageKey,
                "Search"
            ),
            // We only need to set layout-specific properties here.
            // Colors, borders, and interaction states are handled by RHTextField.
            style: {
                width: "100%",
                height: `${RHTables.dataViewSearchFieldHeight}px`,
                minHeight: `${RHTables.dataViewSearchFieldHeight}px`,
                marginTop: "0px"
            }
        })
        
        // Re-attach the listener for the DataView's filtering logic.
        // Guarded against duplicate text: the native <input> fires both `input`
        // (on every keystroke) and `change` (on blur, if the value changed since
        // focus) — both are mapped to TextChange by UITextField. Without this guard,
        // clicking a row after typing blurs the search field, the browser fires a
        // redundant `change` → TextChange, and updateTableDataByFiltering() tears
        // down and rebuilds the rows mid-click, breaking the row's own click handler.
        let lastFilteredSearchText = this.searchTextField.text
        this.searchTextField.addTargetForControlEvent(
            RHTextField.controlEvent.TextChange,
            async () => {
                const currentSearchText = this.searchTextField.text
                if (currentSearchText === lastFilteredSearchText) {
                    return
                }
                lastFilteredSearchText = currentSearchText
                if (this.tableView.isMemberOfViewTree) {
                    await this.updateTableDataByFiltering()
                    this.didUpdateTableAfterSearchTextChange()
                }
            }
        )
        
        // 4. Header Replacement
        
        this.tableHeaderView = new RHTableHeaderView(this.elementID + "Header")
        this._wireTableHeaderViewCallbacks(this.tableHeaderView)
        // Update the ColumnView descriptors so they point to the new header,
        // removing the old one in the process.
        this._gridContainerView.descriptors = [
            ColumnCellDescriptor.intrinsic(this.tableHeaderView),
            ColumnCellDescriptor.scrollFill(this.tableView)
        ]
        this._gridContainerView.style.outline = RHTables.dataViewGridFocusOutline
        this._gridContainerView.style.outlineOffset = RHTables.dataViewGridFocusOutlineOffset
        
        // 5. Row Factory Override
        this.tableView.newReusableViewForIdentifier = (_identifier, rowIDIndex) => {
            const rowView = new RHTableRowView<DataType>(this.elementID + "TableRow" + rowIDIndex)
            rowView.usesCardMode = this.usesRowCardMode
            rowView.treeDisclosureColumnKeyPath = this.treeDisclosureColumnKeyPath
            
            rowView.configureWithObject({
                expandedValueDidChange: async (_row: UIView, event: MouseEvent) => {
                    this.rowExpandedValueDidChange(rowView, event)
                    this.keepRowStaticDuringNextUpdate(rowView)
                    await this.updateTableDataByFiltering()
                    this.setNeedsLayoutUpToRootView()
                    this.displayedRowsDidChangeAfterTreeExpansion?.()
                }
            })
            
            return rowView
        }
        
        UIView.injectCSS(`
            #${this.elementID} .InquiryRowView.even .InquiryRowViewBackground {
                background-color: ${RHTables.rowEvenBackgroundColor.stringValue};
            }
            #${this.elementID} .InquiryRowView.odd .InquiryRowViewBackground {
                background-color: ${RHTables.rowOddBackgroundColor.stringValue};
            }
            #${this.elementID} .InquiryRowView:hover .InquiryRowViewBackground::after {
                content: "";
                position: absolute;
                inset: 0;
                background-color: ${RHTables.rowHoverBackgroundColor.stringValue};
                pointer-events: none;
            }
            #${this.elementID} .InquiryRowView .InquiryRowViewBackground {
                position: relative;
            }
            #${this.elementID} .InquiryRowView {
                box-sizing: border-box;
            }
            #${this.elementID} .InquiryRowView [role="gridcell"] {
                border-bottom: ${RHTables.rowBorderBottomWidth} solid ${RHTables.rowBorderBottomColor.stringValue};
                box-sizing: border-box;
            }
        `)
        
        // No-data message label styling
        this._noDataMessageLabel.configureWithObject({
            textColor: RHTables.dataViewNoDataTextColor,
            textAlignment: UITextView.textAlignment.center,
            style: {
                fontSize: RHTables.dataViewNoDataFontSize,
                fontWeight: RHTables.dataViewNoDataFontWeight,
                lineHeight: RHTables.dataViewNoDataLineHeight,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center"
            }
        })
        this._noDataMessageLabel.textElementView.configureWithObject({
            style: {
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center"
            }
        })
        
    }


    configureMultilineTableHeader() {
        this.tableHeaderView.configureWithObject({
            intrinsicContentHeight: () => RHTables.dataViewMultilineHeaderHeight
        })

        for (const headerCell of this.tableHeaderView.cells) {
            const buttonView = headerCell.buttonView
            if (buttonView) {
                buttonView.configureWithObject({
                    contentPadding: RHTables.headerMultilineSortButtonContentPadding
                })
            }

            const titleLabel = buttonView?.titleLabel ?? headerCell.titleLabel
            if (!titleLabel) {
                continue
            }

            titleLabel.configureWithObject({
                isSingleLine: NO,
                style: {
                    whiteSpace: "normal",
                    lineHeight: RHTables.headerMultilineLineHeight
                }
            })
            titleLabel.textElementView.configureWithObject({
                style: {
                    whiteSpace: "normal",
                    lineHeight: RHTables.headerMultilineLineHeight
                }
            })
        }

        this.tableHeaderView.setNeedsLayout()
        this.tableView.setNeedsLayout()
    }


    static routePageScrollWheelEventInContentView(event: WheelEvent, contentView?: UIView): boolean {
        if (!contentView || event.defaultPrevented || event.ctrlKey) {
            return NO
        }

        let delta = event.deltaY
        if (event.deltaMode === WheelEvent.DOM_DELTA_LINE) {
            delta *= 16
        }
        else if (event.deltaMode === WheelEvent.DOM_DELTA_PAGE) {
            delta *= document.documentElement.clientHeight
        }
        delta /= UIView.pageScale

        if (delta === 0 || Math.abs(event.deltaX) > Math.abs(event.deltaY)) {
            return NO
        }

        const dataView = this.dataViewParticipatingInPageScrollChainForContentView(contentView)
        if (!dataView) {
            return NO
        }

        const pageScrollElement = document.scrollingElement
        if (!pageScrollElement) {
            return NO
        }

        if (dataView.pageScrollForwardingMode === "revealThenBlockPage") {
            return this._routeRevealThenBlockPageScrollWheelEvent(event, delta, dataView, pageScrollElement)
        }

        if (dataView.pageScrollForwardingMode === "revealThenPreferDataview") {
            return this._routeRevealThenPreferDataviewScrollWheelEvent(event, delta, dataView, pageScrollElement)
        }

        const eventTarget = event.target
        if (eventTarget instanceof Node && dataView.tableView.viewHTMLElement.contains(eventTarget)) {
            return NO
        }

        if (this._wheelEventTargetCanScrollInDirection(eventTarget, delta)) {
            return NO
        }

        if (dataView.pageScrollForwardingMode === "pageFirst") {
            return this._routePageFirstScrollWheelEvent(event, delta, dataView, pageScrollElement)
        }

        return this._routeChainedScrollWheelEvent(event, delta, dataView, pageScrollElement)
    }


    static dataViewParticipatingInPageScrollChainForContentView(contentView: UIView): RHDataView<object> | undefined {
        let result: RHDataView<object> | undefined
        let resultArea = 0

        RHDataView._pageScrollChainDataViews.forEach(dataView => {
            if (!this._dataViewIsInContentView(dataView, contentView)) {
                return
            }

            if (dataView.pageScrollForwardingMode === "disabled" || !dataView.isMemberOfViewTree || dataView.hidden) {
                return
            }

            const rectangle = dataView.viewHTMLElement.getBoundingClientRect()
            if (rectangle.bottom <= 0 || rectangle.top >= window.innerHeight ||
                rectangle.right <= 0 || rectangle.left >= window.innerWidth) {
                return
            }

            const visibleWidth = [0, [rectangle.right, window.innerWidth].min() - [rectangle.left, 0].max()].max()
            const visibleHeight = [0, [rectangle.bottom, window.innerHeight].min() - [rectangle.top, 0].max()].max()
            const visibleArea = visibleWidth * visibleHeight
            if (visibleArea > resultArea) {
                result = dataView
                resultArea = visibleArea
            }
        })

        return result
    }


    static _dataViewIsInContentView(dataView: RHDataView<object>, contentView: UIView): boolean {
        if (dataView === contentView) {
            return YES
        }
        let superview = dataView.superview
        while (superview) {
            if (superview === contentView) {
                return YES
            }
            superview = superview.superview
        }
        return NO
    }


    static _routeRevealThenBlockPageScrollWheelEvent(
        event: WheelEvent,
        delta: number,
        dataView: RHDataView<object>,
        pageScrollElement: Element
    ): boolean {
        const boundaryEpsilon = this.pageScrollBoundaryEpsilon
        const pageBoundaryOffset = this._pageScrollBoundaryOffsetForRevealedDataView(dataView, pageScrollElement)
        const currentPageOffset = pageScrollElement.scrollTop
        const tableElement = dataView.tableView.viewHTMLElement
        const tableMaximumOffset = [0, tableElement.scrollHeight - tableElement.clientHeight].max()

        if (this._wheelEventTargetCanScrollInDirection(event.target, delta, tableElement)) {
            return NO
        }

        if (delta > 0) {
            const pageRevealDistance = [0, pageBoundaryOffset - currentPageOffset].max()
            if (pageRevealDistance > delta + boundaryEpsilon) {
                return NO
            }

            if (tableElement.scrollTop >= tableMaximumOffset - boundaryEpsilon) {
                return NO
            }

            event.preventDefault()

            const pageConsumedDistance = [pageRevealDistance, delta].min()
            if (pageConsumedDistance > boundaryEpsilon) {
                pageScrollElement.scrollTop += pageConsumedDistance
            }
            dataView._consumePageScrollChainDelta(delta - pageConsumedDistance)
            return YES
        }

        if (tableElement.scrollTop <= boundaryEpsilon) {
            return NO
        }

        event.preventDefault()
        dataView._consumePageScrollChainDelta(delta)
        return YES
    }


    static _routeRevealThenPreferDataviewScrollWheelEvent(
        event: WheelEvent,
        delta: number,
        dataView: RHDataView<object>,
        pageScrollElement: Element
    ): boolean {
        const boundaryEpsilon = this.pageScrollBoundaryEpsilon
        const pageBoundaryOffset = this._pageScrollBoundaryOffsetForRevealedDataView(dataView, pageScrollElement)
        const currentPageOffset = pageScrollElement.scrollTop
        const tableElement = dataView.tableView.viewHTMLElement
        const pageMaximumOffset = [0, pageScrollElement.scrollHeight - pageScrollElement.clientHeight].max()
        const tableMaximumOffset = [0, tableElement.scrollHeight - tableElement.clientHeight].max()
        const pageIsAtBottom = currentPageOffset >= pageMaximumOffset - boundaryEpsilon
        const gestureState = this._revealThenPreferDataviewWheelGestureStateForEvent(
            event,
            dataView,
            currentPageOffset >= pageBoundaryOffset - boundaryEpsilon
        )

        if (!gestureState.didStartInRevealedState && !pageIsAtBottom) {
            return NO
        }

        if (this._wheelEventTargetCanScrollInDirection(event.target, delta, tableElement)) {
            return NO
        }

        if (delta > 0 && tableElement.scrollTop >= tableMaximumOffset - boundaryEpsilon) {
            return NO
        }
        if (delta < 0 && tableElement.scrollTop <= boundaryEpsilon) {
            return NO
        }

        event.preventDefault()
        dataView._consumePageScrollChainDelta(delta)
        return YES
    }


    static _revealThenPreferDataviewWheelGestureStateForEvent(
        event: WheelEvent,
        dataView: RHDataView<object>,
        didStartInRevealedState: boolean
    ): { lastWheelEventTime: number; didStartInRevealedState: boolean } {
        const previousState = this._revealThenPreferDataviewWheelGestureStates.get(dataView)
        let gestureDidStartInRevealedState = didStartInRevealedState
        if (previousState &&
            event.timeStamp - previousState.lastWheelEventTime < this.pageScrollWheelGestureResetInterval) {
            gestureDidStartInRevealedState = previousState.didStartInRevealedState
        }

        const gestureState = {
            lastWheelEventTime: event.timeStamp,
            didStartInRevealedState: gestureDidStartInRevealedState
        }
        this._revealThenPreferDataviewWheelGestureStates.set(dataView, gestureState)
        return gestureState
    }


    static _pageScrollBoundaryOffsetForRevealedDataView(
        dataView: RHDataView<object>,
        pageScrollElement: Element
    ): number {
        const rectangle = dataView.viewHTMLElement.getBoundingClientRect()
        const viewportHeight = pageScrollElement.clientHeight
        const dataViewHeight = rectangle.bottom - rectangle.top
        const pageMaximumOffset = [0, pageScrollElement.scrollHeight - pageScrollElement.clientHeight].max()

        let boundaryOffset = pageScrollElement.scrollTop + (rectangle.bottom - viewportHeight) / UIView.pageScale
        if (dataViewHeight > viewportHeight) {
            boundaryOffset = pageScrollElement.scrollTop + rectangle.top / UIView.pageScale
        }

        return [0, [pageMaximumOffset, boundaryOffset].min()].max()
    }


    static _routeChainedScrollWheelEvent(
        event: WheelEvent,
        delta: number,
        dataView: RHDataView<object>,
        pageScrollElement: Element
    ): boolean {
        const boundaryEpsilon = this.pageScrollBoundaryEpsilon
        const pageMaximumOffset = [0, pageScrollElement.scrollHeight - pageScrollElement.clientHeight].max()
        const pageRemainingDistance = [0, pageMaximumOffset - pageScrollElement.scrollTop].max()
        const tableElement = dataView.tableView.viewHTMLElement

        if (delta > 0) {
            if (pageRemainingDistance > delta + boundaryEpsilon) {
                return NO
            }

            const tableMaximumOffset = [0, tableElement.scrollHeight - tableElement.clientHeight].max()
            const tableRemainingDistance = [0, tableMaximumOffset - tableElement.scrollTop].max()
            if (pageRemainingDistance <= boundaryEpsilon && tableRemainingDistance <= boundaryEpsilon) {
                return NO
            }

            event.preventDefault()

            const pageConsumedDistance = [pageRemainingDistance, delta].min()
            pageScrollElement.scrollTop += pageConsumedDistance
            const tableDelta = delta - pageConsumedDistance
            dataView._consumePageScrollChainDelta(tableDelta)
            return YES
        }

        const pageIsAtBottom = pageRemainingDistance <= boundaryEpsilon
        const tableHasConsumedScroll = tableElement.scrollTop > boundaryEpsilon
        if (!pageIsAtBottom || !tableHasConsumedScroll) {
            return NO
        }

        event.preventDefault()
        const unconsumedDelta = dataView._consumePageScrollChainDelta(delta)
        if (unconsumedDelta < -boundaryEpsilon) {
            pageScrollElement.scrollTop += unconsumedDelta
        }
        return YES
    }


    static _routePageFirstScrollWheelEvent(
        event: WheelEvent,
        delta: number,
        dataView: RHDataView<object>,
        pageScrollElement: Element
    ): boolean {
        const boundaryEpsilon = this.pageScrollBoundaryEpsilon
        const pageMaximumOffset = [0, pageScrollElement.scrollHeight - pageScrollElement.clientHeight].max()
        const tableElement = dataView.tableView.viewHTMLElement
        const tableMaximumOffset = [0, tableElement.scrollHeight - tableElement.clientHeight].max()

        if (delta > 0) {
            if (pageScrollElement.scrollTop < pageMaximumOffset - boundaryEpsilon) {
                return NO
            }

            if (tableElement.scrollTop >= tableMaximumOffset - boundaryEpsilon) {
                return NO
            }
        }
        else {
            if (pageScrollElement.scrollTop > boundaryEpsilon) {
                return NO
            }

            if (tableElement.scrollTop <= boundaryEpsilon) {
                return NO
            }
        }

        event.preventDefault()
        dataView._consumePageScrollChainDelta(delta)
        return YES
    }


    static _wheelEventTargetCanScrollInDirection(
        eventTarget: EventTarget | null,
        delta: number,
        scrollableElementToIgnore?: Element
    ): boolean {
        let element: Element | undefined
        if (eventTarget instanceof Element) {
            element = eventTarget
        }

        while (element && element !== document.body && element !== document.documentElement) {
            const htmlElement = element as HTMLElement
            const maximumOffset = htmlElement.scrollHeight - htmlElement.clientHeight
            if (maximumOffset > this.pageScrollBoundaryEpsilon) {
                const computedStyle = window.getComputedStyle(htmlElement)
                const allowsScrolling = computedStyle.overflowY === "auto" || computedStyle.overflowY === "scroll"
                if (allowsScrolling && htmlElement !== scrollableElementToIgnore) {
                    if (delta > 0 && htmlElement.scrollTop < maximumOffset - this.pageScrollBoundaryEpsilon) {
                        return YES
                    }
                    if (delta < 0 && htmlElement.scrollTop > this.pageScrollBoundaryEpsilon) {
                        return YES
                    }
                }
            }
            element = element.parentElement ?? undefined
        }

        return NO
    }


    _consumePageScrollChainDelta(delta: number): number {
        if (delta === 0) {
            return 0
        }

        const tableElement = this.tableView.viewHTMLElement
        const previousOffset = tableElement.scrollTop
        const maximumOffset = [0, tableElement.scrollHeight - tableElement.clientHeight].max()
        tableElement.scrollTop = [0, [maximumOffset, previousOffset + delta].min()].max()
        const consumedDelta = tableElement.scrollTop - previousOffset
        return delta - consumedDelta
    }


    override set descriptors(descriptors: (CBDataViewCellDescriptor<DataType> | CBSelectionColumnDescriptor<DataType>)[]) {
        super.descriptors = descriptors.map(descriptor => {
            const cellDescriptor = descriptor as CBDataViewCellDescriptor<DataType>
            const defaultAlignment = cellDescriptor.buttonWasPressed
                                     ? UITextView.textAlignment.center
                                     : UITextView.textAlignment.left
            const alignment = cellDescriptor.alignment ?? defaultAlignment
            return Object.assign(
                {
                    titleLabelAlignment: alignment,
                    cellLabelAlignment: alignment
                } satisfies Partial<CBDataViewCellDescriptor<DataType>>,
                descriptor
            )
        })
    }
    
    override get descriptors() {
        return super.descriptors
    }
    
    override get noDataMessage() {
        return super.noDataMessage
    }
    
    override set noDataMessage(message: string) {
        super.noDataMessage = message
        this.layoutNoDataMessageIfPossible()
    }
    
    override get filteredData() {
        return super.filteredData
    }
    
    override set filteredData(value: CBDataViewRow<DataType>[]) {
        super.filteredData = value
        this.layoutNoDataMessageIfPossible()
        requestAnimationFrame(() => this.layoutNoDataMessageIfPossible())
    }
    
    
    override didUpdateTableAfterSearchTextChange = () => {
        // Override to react after the table has been updated following a search text change.
    }
    
    override layoutNoDataMessageIfPossible() {
        if (this.bounds.width <= 0) {
            return
        }
        
        const padding = this.core.paddingLength
        const bounds = this.contentBoundsWithInset(padding)
        const gridFrame = this._gridContainerView.frame
        const headerFrame = this.tableHeaderView.frame
        const searchFieldFrame = this.searchTextField.frame
        
        let noDataMessageRowFrame = gridFrame
        let headerBottomY = gridFrame.y + RHTables.dataViewTableHeaderHeight
        if (gridFrame.width <= 0) {
            if (searchFieldFrame.width <= 0) {
                return
            }
            noDataMessageRowFrame = bounds
            let searchAreaBottom = searchFieldFrame.y + RHTables.dataViewSearchFieldHeight
            const searchAccessoryView = this._searchAccessoryView
            if (searchAccessoryView && searchAccessoryView.superview === this && !searchAccessoryView.hidden) {
                searchAreaBottom += RHTables.dataViewSearchAccessoryTopGap
                    + searchAccessoryView.intrinsicContentHeight(bounds.width)
            }
            headerBottomY = searchAreaBottom +
                padding +
                RHTables.dataViewTableHeaderHeight
        }
        else if (headerFrame.height > 0) {
            headerBottomY = gridFrame.y + headerFrame.max.y
        }
        
        this._noDataMessageLabel.frame = noDataMessageRowFrame
            .rectangleWithY(headerBottomY + RHTables.dataViewNoDataMessageTopGap)
            .rectangleWithHeight(RHTables.dataViewNoDataMessageHeight)
            .rectangleWithWidth(bounds.width, 0.5)
        this._noDataMessageLabel.textElementView.frame = this._noDataMessageLabel.bounds
        if (!this._noDataMessageLabel.hidden) {
            this._noDataMessageLabel.moveToTopOfSuperview()
        }
    }
    
    
    override layoutSubviews() {
        super.layoutSubviews()
        
        if (this.searchTextField.hidden) {
            this.layoutNoDataMessageIfPossible()
            return
        }
        
        const padding = this.core.paddingLength
        const labelHeight = padding * 2
        const bounds = this.contentBoundsWithInset(padding)
        const titleLabelFrame = IF(this.titleLabel.text)(() =>
            bounds.rectangleWithHeight(labelHeight)
        ).ELSE(() =>
            bounds.rectangleWithHeight(0)
        )
        
        this.titleLabel.frame = titleLabelFrame
        
        const searchFieldRowFrame = IF(this.titleLabel.text)(() =>
            titleLabelFrame.rectangleForNextRow(padding * 0.5)
        ).ELSE(() =>
                bounds
            )
            // .rectangleWithHeight(padding * 1.5)
            .rectangleWithHeight(RHTables.dataViewSearchFieldHeight)
        
        this.searchTextField.frame = searchFieldRowFrame

        // Opt-in accessory row directly under the search field (e.g. the CPV-search chip).
        let previousFrameFromTableHeader = searchFieldRowFrame
        const searchAccessoryView = this._searchAccessoryView
        if (searchAccessoryView && searchAccessoryView.superview === this && !searchAccessoryView.hidden) {
            previousFrameFromTableHeader = searchFieldRowFrame
                .rectangleForNextRow(RHTables.dataViewSearchAccessoryTopGap, searchAccessoryView)
                .assignedAsFrameOfView(searchAccessoryView)
        }

        this._gridContainerView.frame = previousFrameFromTableHeader
            .IF(!this.isVirtualLayouting && bounds.height > 0)
            .rectangleForNextRow(
                padding,
                [0, bounds.height - previousFrameFromTableHeader.max.y - padding].max()
            )
            .ELSE()
            .rectangleForNextRow(padding, this._gridContainerView)
            .ENDIF()
        this._applyAutomaticColumnSizing()
        
        this.layoutNoDataMessageIfPossible()
    }
    
    //
    //             const weight = weights[i]
    //             const minWidth = minWidths[i]
    //
    //             const widthWithCurrentWeight = (weight / weights.summedValue) * availableTotalWidth
    //
    //             const needsExpanding = widthWithCurrentWeight < minWidth
    //
    //             weightDescriptors.push({
    //
    //                 index: i,
    //                 currentWeight: weight,
    //                 minWidth: minWidth,
    //                 widthWithCurrentWeight: widthWithCurrentWeight,
    //                 widthLeftAboveMin: widthWithCurrentWeight - minWidth,
    //                 needsExpanding: needsExpanding
    //
    //             })
    //
    //
    //         }
    //
    //         const sortedWeightDescriptors = weightDescriptors.copy().sort((
    //             a,
    //             b
    //         ) => (a.widthLeftAboveMin) - (b.widthLeftAboveMin))
    //
    //         const weightDescriptorsThatNeedExpanding = sortedWeightDescriptors.filter(
    //             descriptor => descriptor.needsExpanding
    //         )
    //
    //         const weightDescriptorsThatHaveSpace = sortedWeightDescriptors.filter(
    //             descriptor => !descriptor.needsExpanding
    //         )
    //
    //         const totalSpace =
    // weightDescriptorsThatHaveSpace.everyElement.widthLeftAboveMin.UI_elementValues?.summedValue ?? 0  const
    // totalSpaceNeededForExpansion = weightDescriptorsThatNeedExpanding.map( descriptor =>
    // -descriptor.widthLeftAboveMin).summedValue  weightDescriptorsThatNeedExpanding.forEach(descriptor =>
    // weights[descriptor.index] = descriptor.minWidth) weightDescriptorsThatHaveSpace.forEach(descriptor => {  const
    // spaceChangeMultiplier = descriptor.widthLeftAboveMin / totalSpace  weights[descriptor.index] =
    // descriptor.widthWithCurrentWeight - totalSpaceNeededForExpansion * spaceChangeMultiplier  })
    // this.tableHeaderView.cellWeights = weights this.tableView.cellWeights = weights  this.tableView.setNeedsLayout()
    //   }   }
    
    //asdasd
    // override intrinsicContentHeight = (constrainingWidth: number = 0): number => {
    //
    //     const padding = this.core.paddingLength
    //     const labelHeight = padding
    //
    //     var result = padding * 2 + padding * 2 + padding + 50 + //padding +
    //         this.tableView.intrinsicContentHeight(constrainingWidth) + padding * 1.5 //* 2//.5
    //
    //     return result
    //
    // }
    
    
}
